import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../lib/theme";
import { formatINR } from "../lib/format";

export interface MiniBar {
  label: string;
  value: number;
}

// Horizontal-comparison bar chart. Used to put a visual next to a sortable
// table — e.g. broker × mode P&L next to the corresponding table rows.

export default function MiniBarChart({
  bars,
  height = 180,
}: {
  bars: MiniBar[];
  height?: number;
}) {
  const { resolved } = useTheme();
  const isDark = resolved === "dark";
  const grid = isDark ? "#334155" : "#e2e8f0";
  const axis = isDark ? "#94a3b8" : "#64748b";
  if (bars.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-xs text-muted"
      >
        Nothing to chart yet.
      </div>
    );
  }
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={bars}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 16 }}
        >
          <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            stroke={axis}
            fontSize={11}
            tickFormatter={(v: number) =>
              Math.abs(v) >= 1000
                ? `${(v / 1000).toFixed(0)}k`
                : v.toFixed(0)
            }
          />
          <YAxis
            type="category"
            dataKey="label"
            stroke={axis}
            fontSize={11}
            width={110}
          />
          <Tooltip
            contentStyle={{
              background: isDark ? "#0b1220" : "#ffffff",
              border: `1px solid ${grid}`,
              borderRadius: 8,
              fontSize: 12,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
            formatter={(v) => [formatINR(Number(v), { sign: true }), "P&L"]}
            cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }}
          />
          <Bar dataKey="value" radius={[2, 2, 2, 2]} isAnimationActive={false}>
            {bars.map((b, i) => (
              <Cell key={i} fill={b.value >= 0 ? "#10b981" : "#f43f5e"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
